import type { InputProps } from '@chakra-ui/react';
import { Box, Input } from '@chakra-ui/react';
import type { ReactNode } from 'react';
import React, { forwardRef } from 'react';

import type { DisplayCardProps } from './DisplayCard';
import { DisplayCard } from './DisplayCard';

type InputCardProps = Omit<DisplayCardProps, 'onChange'> & {
  value: string;
  placeholder?: string;
  onChange?: InputProps['onChange'];
  onFocus?: InputProps['onFocus'];
  onBlur?: InputProps['onBlur'];
  label?: ReactNode;
  unit?: string;
};

export const InputCard = forwardRef<HTMLInputElement, InputCardProps>(
  (
    {
      value,
      placeholder,
      onChange,
      onFocus,
      onBlur,
      usd,
      label,
      unit,
      ...props
    },
    ref,
  ) => {
    return (
      <DisplayCard
        position="relative"
        usd={value.trim() ? usd : null}
        {...props}
      >
        {label && (
          <Box
            textStyle="caption"
            position="absolute"
            top="8px"
            left={{ base: '18px', desktop: '26px' }}
          >
            {label}
          </Box>
        )}
        {unit && (
          <Box
            mt={label ? '8px' : 0}
            position="absolute"
            right="24px"
            textStyle="body1"
          >
            {unit}
          </Box>
        )}
        <Input
          mt={label ? '12px' : 0}
          disabled={props.disabled}
          readOnly={props.readOnly}
          ref={ref}
          h="24px"
          type="number"
          min="0"
          placeholder={placeholder ?? 'input' + '...'}
          onChange={onChange}
          value={value}
          onFocus={onFocus}
          onKeyDown={(e) =>
            ['e', 'E', '+', '-'].includes(e.key) && e.preventDefault()
          }
          onWheel={(e) => e.currentTarget.blur()}
          onBlur={onBlur}
          color={props.error ? 'state.error' : 'black'}
          p="0"
          border="none"
          _focus={{ outline: 'none', boxShadow: 'none', border: 'none' }}
          _placeholder={{
            color: 'text.secondary',
          }}
          textStyle="body1Bold"
          css={{
            '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
              WebkitAppearance: 'none',
              margin: 0,
            },
            '&[type=number]': {
              MozAppearance: 'textfield',
            },
          }}
        />
      </DisplayCard>
    );
  },
);

InputCard.displayName = 'InputCard';
